import { useMemo, useState, FormEvent, ChangeEvent } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { resources } from "@constants/hardcoded-strings";
import { VERIFICATION_SERVICE_CREATE_METHOD_OPTIONS } from "@constants/verification-service-create-method-options";
import { ButtonTypes } from "@enums/button-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { VerificationServiceCreateMethodTypes } from "@enums/verification-service-create-method-types";
import { SelectOption } from "@idw-types/common";
import { FormattedVerificationServiceConfiguration } from "@idw-types/data/verification-service-configuration";
import { RpServiceTemplate } from "@idw-types/data/reference-data/generic";
import { AxnAdminUserPermissions } from "@idw-types/data/axn-admin-user";
import { EntityImportValidationError } from "@idw-types/data/entity-import-validation-error";
import {
    validateVerificationServiceRequest
} from "@requests/service-configuration";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";
import { navigateToUtil } from "@utils/navigation-util";
import { injectRpServiceId } from "@utils/parameter-injector";
import { generateImportErrorMessages } from "@utils/entity-import/entity-import-util";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import {
    RelyingPartyServiceTemplateSelectFormGroup,
    VerificationServiceSelectFormGroup
} from "@containers/common/select-form-groups";
import { DefaultModal } from "@containers/common/modals/default-modal/default-modal";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import defaultStyles from "@containers/common/modals/default-modal/default-modal.less";
import styles from "./add-verification-service-modal.less";
import { VerificationServiceChangeRequestResponse } from "@idw-types/data/verification-service/verification-service-change-request-response";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import { GenericNetworkResponse } from "@idw-types/generic-network-response";
import { CreateWorkflowVerificationServiceResponse } from "@requests/service-configuration/create-workflow-verification-service";

type AddVerificationServiceModalProps = {
    onModalClose: () => void;
    verificationServices: Array<FormattedVerificationServiceConfiguration> | Array<AxnRpService>;
    rpServiceTemplates: Array<RpServiceTemplate>;
    redirectUrl: string;
    userPermissions: AxnAdminUserPermissions;
    title?: string;
    description?: string;
    createServiceRequest: (rpServiceTemplateId: string, sourceRPServiceID: string, body: Record<string, any>) => Promise<GenericNetworkResponse<VerificationServiceChangeRequestResponse | CreateWorkflowVerificationServiceResponse>>
    updateStateForNewService: (data: CreateWorkflowVerificationServiceResponse) => void;
};

type AddVerificationServiceModalState = {
    requestInProgress: boolean;
    selectedCreateMethod: SelectOption;
    selectedRpServiceTemplateOption: SelectOption;
    selectedFileName: string;
    selectedFile: File;
    selectedVerificationServiceOption: SelectOption;
};

type AddVerificationServiceModalValidationState = {
    selectedFileJSON: any;
    importErrors: Array<EntityImportValidationError>;
    validationComplete: boolean;
};

export const AddVerificationServiceModal = (props: AddVerificationServiceModalProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const {
        title = resources.modals.addVerificationService.title,
        description = resources.modals.addVerificationService.description,
        onModalClose,
        verificationServices,
        rpServiceTemplates,
        userPermissions,
        redirectUrl,
        updateStateForNewService,
        createServiceRequest
    } = props;
    const [labelWidth] = useState(LabelWidthTypes.SMALL);

    const availableServiceCreationMethodOptions = useMemo(() => {
        const finalList: Array<SelectOption> = [];
        if (userPermissions.canCreateRPServiceFromTemplate) {
            finalList.push(VERIFICATION_SERVICE_CREATE_METHOD_OPTIONS[0]);
        }
        if (userPermissions.canCreateRPServiceFromScratch) {
            finalList.push(VERIFICATION_SERVICE_CREATE_METHOD_OPTIONS[1]);
        }
        if (userPermissions.canCreateRPServiceFromImportFile) {
            finalList.push(VERIFICATION_SERVICE_CREATE_METHOD_OPTIONS[2]);
        }
        if (userPermissions.canCreateRPServiceFromExisting) {
            finalList.push(VERIFICATION_SERVICE_CREATE_METHOD_OPTIONS[3]);
        }
        return finalList;
    }, [userPermissions]);

    const [state, setState] = useState<AddVerificationServiceModalState>({
        requestInProgress: false,
        selectedCreateMethod: availableServiceCreationMethodOptions[0] || null,
        selectedRpServiceTemplateOption: null,
        selectedFileName: "",
        selectedFile: null,
        selectedVerificationServiceOption: null,
    });
    const [validationState, setValidationState] = useState<AddVerificationServiceModalValidationState>({
        selectedFileJSON: null,
        importErrors: [],
        validationComplete: false
    });

    const redirectTo = (rpServiceID: string) => {
        const url = injectRpServiceId(redirectUrl, rpServiceID);
        navigateToUtil(url, navigate, routerLocation.pathname);
    }

    const handlePostCreateVerificationService = async (data: VerificationServiceChangeRequestResponse | CreateWorkflowVerificationServiceResponse, responsePropertyKeys: Array<string>) => {
        const rpServiceID = getRpServiceID(data, responsePropertyKeys);

        displaySuccessModal(
            dispatch,
            resources.requests.createVerificationService.success.title,
            resources.requests.createVerificationService.success.message
        );

        if (redirectUrl) {
            redirectTo(rpServiceID);
        }

        if (updateStateForNewService) {
            updateStateForNewService(data as CreateWorkflowVerificationServiceResponse);
        }

        onModalClose();
    }

    const isCreateWorkflowRpServiceResponse = (responsePropertyKeys: Array<string>) => {
        return (responsePropertyKeys.includes('axnAdminRPService') && responsePropertyKeys.includes('axnAdminRPServiceWorkflow'));
    }

    const hasResponse = (responsePropertyKeys: Array<string>) => {
        return responsePropertyKeys.includes('changeRequest') || isCreateWorkflowRpServiceResponse(responsePropertyKeys);
    }

    const getRpServiceID = (data: VerificationServiceChangeRequestResponse | CreateWorkflowVerificationServiceResponse, responsePropertyKeys: Array<string>) => {
        let rpServiceID;
        if (responsePropertyKeys.includes('changeRequest')) {
            const { changeRequest } = data as VerificationServiceChangeRequestResponse;
            rpServiceID = changeRequest.rpServiceID;
        } else if (responsePropertyKeys.includes('axnAdminRPService')) {
            const { axnAdminRPService } = data as CreateWorkflowVerificationServiceResponse;
            rpServiceID = axnAdminRPService.id;
        }

        return rpServiceID;
    }

    const handleUseTemplateSubmit = async () => {
        const { data, error, queryParameters: queryParams } = await createServiceRequest(state.selectedRpServiceTemplateOption.value, null, undefined);
        const keys = Object.keys(data || {});

        if (hasResponse(keys)) {
            handlePostCreateVerificationService(data, keys);
        } else {
            if (error) {
                logAndDisplayErrorModal(
                    dispatch,
                    error,
                    resources.requests.createVerificationService.error.title,
                    resources.requests.createVerificationService.error.message,
                    null,
                    queryParams
                );
            }
            setState({ ...state, requestInProgress: false });
        }
    };

    const handleUseBlankSubmit = async () => {
        const { data, error, queryParameters: queryParams } = await createServiceRequest(null, null, undefined);
        const keys = Object.keys(data || {});

        if (hasResponse(keys)) {
            handlePostCreateVerificationService(data, keys);
        } else {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.createVerificationService.error.title,
                resources.requests.createVerificationService.error.message,
                null,
                queryParams
            );
            setState({ ...state, requestInProgress: false });
        }
    };

    const handleUseValidateSubmit = async () => {
        const { data, error } = await validateVerificationServiceRequest(validationState.selectedFileJSON);
        if (data?.length > 0) {
            setValidationState({ ...validationState, importErrors: data, validationComplete: true });
            setState({ ...state, requestInProgress: false });
        } else if (data?.length === 0) {
            setValidationState({ ...validationState, importErrors: [], validationComplete: true });
            setState({ ...state, requestInProgress: false });
        } else {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.validateVerificationService.error.title,
                resources.requests.validateVerificationService.error.message
            );
            setState({ ...state, requestInProgress: false });
        }
    };

    const handleUseImportSubmit = async () => {
        const { data, error, queryParameters: queryParams, body } = await createServiceRequest(null, null, validationState.selectedFileJSON);
        const keys = Object.keys(data || {});

        if (hasResponse(keys)) {
            handlePostCreateVerificationService(data, keys);
        } else {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.createVerificationService.error.title,
                resources.requests.createVerificationService.error.message,
                null,
                queryParams,
                body
            );
            setState({ ...state, requestInProgress: false });
        }
    };

    const updateRpServiceNameFromCopy = (data: CreateWorkflowVerificationServiceResponse) => {
        const rpServiceIndex = verificationServices.findIndex(s => s.id === state.selectedVerificationServiceOption.value);
        if (rpServiceIndex !== -1) {
            const serviceName = `Copy of ${verificationServices[rpServiceIndex].serviceName}`;
            const { axnAdminRPService } = data;

            return {
                ...data,
                axnAdminRPService: {
                    ...axnAdminRPService,
                    serviceName
                }
            } as CreateWorkflowVerificationServiceResponse;
        }

        return data;

    };

    const handleUseCopySubmit = async () => {
        const { data, error, queryParameters } = await createServiceRequest(null, state.selectedVerificationServiceOption.value, undefined);
        const keys = Object.keys(data || {});

        if (hasResponse(keys)) {
            // set the right name of the service (case for add service at workflow level)
            if (isCreateWorkflowRpServiceResponse(keys)) {
                handlePostCreateVerificationService(updateRpServiceNameFromCopy(data as CreateWorkflowVerificationServiceResponse), keys);
            } else {
                handlePostCreateVerificationService(data, keys);
            }

        } else {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.createVerificationService.error.title,
                resources.requests.createVerificationService.error.message,
                null,
                queryParameters
            );
            setState({ ...state, requestInProgress: false });
        }
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setState({ ...state, requestInProgress: true });

        switch (state.selectedCreateMethod.value) {
            case VerificationServiceCreateMethodTypes.USE_TEMPLATE:
                return handleUseTemplateSubmit();
            case VerificationServiceCreateMethodTypes.USE_BLANK:
                return handleUseBlankSubmit();
            case VerificationServiceCreateMethodTypes.USE_IMPORT:
                if (validationState.validationComplete) {
                    return handleUseImportSubmit();
                } else {
                    return handleUseValidateSubmit();
                }
            case VerificationServiceCreateMethodTypes.USE_COPY:
                return handleUseCopySubmit();
        }
    };

    const handleUpdateIWantTo = (newValue: SelectOption) => {
        if (newValue) {
            setState({
                ...state,
                selectedFileName: "",
                selectedFile: null,
                selectedCreateMethod: newValue
            });
            setValidationState({ selectedFileJSON: null, importErrors: [], validationComplete: false });
        }
    };

    const handleUpdateRpServiceTemplate = (newValue: SelectOption) => {
        if (newValue) {
            setState({ ...state, selectedRpServiceTemplateOption: newValue });
        }
    };

    const handleUpdateFile = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files.length > 0) {
            setState({ ...state, selectedFileName: event.target.value || "", selectedFile: event.target.files[0] });

            try {
                const fileReader = new FileReader();
                fileReader.onload = function () {
                    const jsonVerificationService = JSON.parse(fileReader.result as string);
                    setValidationState({ selectedFileJSON: jsonVerificationService, importErrors: [], validationComplete: false });
                };
                fileReader.readAsText(event.target.files[0]);
            } catch (error) {
                setState({ ...state, selectedFileName: "", selectedFile: null });
                setValidationState({ selectedFileJSON: null, importErrors: [], validationComplete: false });
                logAndDisplayErrorModal(
                    dispatch,
                    error,
                    resources.modals.importVerificationServiceConfiguration.loadFile.error.title,
                    resources.modals.importVerificationServiceConfiguration.loadFile.error.message
                );
            }
        } else {
            setState({ ...state, selectedFileName: "", selectedFile: null });
            setValidationState({ selectedFileJSON: null, importErrors: [], validationComplete: false });
        }
    };

    const handleUpdateVerificationService = (newValue: SelectOption) => {
        if (newValue) {
            setState({ ...state, selectedVerificationServiceOption: newValue });
        }
    };

    const buttonLabel = useMemo(() => {
        switch (state.selectedCreateMethod.value) {
            case VerificationServiceCreateMethodTypes.USE_TEMPLATE:
                return resources.modals.addVerificationService.buttons.add.label;
            case VerificationServiceCreateMethodTypes.USE_BLANK:
                return resources.modals.addVerificationService.buttons.create.label;
            case VerificationServiceCreateMethodTypes.USE_COPY:
                return resources.modals.addVerificationService.buttons.copy.label;
            case VerificationServiceCreateMethodTypes.USE_IMPORT:
                if (validationState.validationComplete) {
                    return resources.common.buttons.continue.label;
                } else {
                    return resources.modals.addVerificationService.buttons.validate.label;
                }
            default:
                return resources.common.buttons.save.label;
        }
    }, [state.selectedCreateMethod, validationState.validationComplete]);

    const errorMessages = useMemo(() => {
        return generateImportErrorMessages(validationState.importErrors);
    }, [validationState.importErrors]);

    return (
        <DefaultModal
            automationId="add-verification-service-modal"
            ariaLabelledBy="add-verification-service-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-verification-service-title"} closeButton>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <span className={defaultStyles.description}>
                        {description}
                    </span>
                    <SelectFormGroup
                        controlId={`verification-service-iwantto-select`}
                        label={resources.modals.addVerificationService.inputs.iWantTo.label}
                        ariaLabel={resources.modals.addVerificationService.inputs.iWantTo.label}
                        automationId={`verification-service-iwantto-select`}
                        onChange={handleUpdateIWantTo}
                        options={availableServiceCreationMethodOptions}
                        selectedOption={state.selectedCreateMethod}
                        disabled={state.requestInProgress}
                        isRequired={true}
                        isSearchable={false}
                        isClearable={false}
                        labelWidth={labelWidth}
                    />
                    {state.selectedCreateMethod.value === VerificationServiceCreateMethodTypes.USE_TEMPLATE && (
                        <RelyingPartyServiceTemplateSelectFormGroup
                            idPrefix={"verification-service"}
                            onChange={handleUpdateRpServiceTemplate}
                            templates={rpServiceTemplates}
                            selectedOption={state.selectedRpServiceTemplateOption}
                            disabled={state.requestInProgress}
                            isRequired={true}
                            isSearchable={false}
                            isClearable={false}
                            labelWidth={labelWidth}
                        />
                    )}
                    {state.selectedCreateMethod.value === VerificationServiceCreateMethodTypes.USE_IMPORT && (
                        <>
                            <InputFormGroup
                                controlId="verification-service-file-input"
                                label={resources.modals.addVerificationService.inputs.file.label}
                                ariaLabel={resources.modals.addVerificationService.inputs.file.label}
                                automationId="verification-service-file-input"
                                value={state.selectedFileName}
                                type={"file"}
                                accept={"application/json"}
                                onChange={handleUpdateFile}
                                disabled={state.requestInProgress}
                                isRequired={true}
                                labelWidth={labelWidth}
                            />
                            {validationState.importErrors.length > 0 && validationState.validationComplete && (
                                <div className={styles.validationContainer}>
                                    <p><b>There were some errors found with contents of the JSON file.</b></p>
                                    {errorMessages.map((error, index) => {
                                        return <p key={`import-error-${index}`}>- {error}</p>;
                                    })}
                                    <p><b>Please fix the issues listed above to continue.</b></p>
                                </div>
                            )}
                            {validationState.importErrors.length === 0 && validationState.validationComplete && (
                                <div className={styles.validationContainer}>
                                    <p><b>The Verification Service was successfully validated. Please click Continue to proceed to the configuration page.</b></p>
                                </div>
                            )}
                        </>
                    )}
                    {state.selectedCreateMethod.value === VerificationServiceCreateMethodTypes.USE_COPY && (
                        <VerificationServiceSelectFormGroup
                            idPrefix={"verification-service"}
                            onChange={handleUpdateVerificationService}
                            verificationServices={verificationServices}
                            selectedOption={state.selectedVerificationServiceOption}
                            disabled={state.requestInProgress}
                            isRequired={true}
                            isSearchable={true}
                            isClearable={false}
                            useVirtualList={true}
                            labelWidth={labelWidth}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button id="add-verification-service-cancel" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <ButtonWithSpinner
                        id="add-verification-service-submit"
                        disabled={state.requestInProgress || errorMessages.length > 0}
                        hideSpinner={errorMessages.length > 0}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                        label={buttonLabel}
                    />
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};